<script lang="ts">
import CarouselScrollBox from '@/components/carousel/CarouselScrollBox.vue'
import type { FilterFunctionProperties, MenuItem, Nullable } from '@/types'
import { useBrowserLocation } from '@vueuse/core'
import { type SlotsType, defineComponent, useTemplateRef } from 'vue'
import { useRouter } from 'vue-router'

import CarouselItem from '../carousel/CarouselItem.vue'
import AppFilterMenuItem from './AppFilterMenuItem.vue'

export default defineComponent({
  components: { AppFilterMenuItem, CarouselScrollBox, CarouselItem },
  props: {
    id: {
      type: String,
      required: true,
    },
    filter: {
      type: Object as () => FilterFunctionProperties<any>,
      required: true,
    },
  },
  emits: ['updateFilter'],
  slots: Object as SlotsType<{
    default?: (props: {
      /** The menu item to display information for */
      item: MenuItem
      /** Indicates if the item is current selected */
      selected: boolean
      /** Function to update the filter to this item */
      updateFilter: (item: MenuItem) => void
    }) => any
  }>,
  setup() {
    return {
      scrollbox:
        useTemplateRef<InstanceType<typeof CarouselScrollBox>>('scrollbox'),
      router: useRouter(),
    }
  },
  data() {
    return {
      current: 0,
    }
  },
  watch: {
    'filter.selectedItemCode.value': {
      handler() {
        const selectedMenuItem = this.getSelectedMenuItem()

        if (!selectedMenuItem) {
          return
        }

        this.updateFilter(selectedMenuItem)
      },
      immediate: true,
    },
    'filter.categories.value': {
      handler() {
        const selectedMenuItem = this.getSelectedMenuItem()

        if (!selectedMenuItem) {
          return
        }

        this.updateFilter(selectedMenuItem)
      },
      immediate: true,
    },
    'router.currentRoute.value.query.tab': {
      handler() {
        this.handleQueryChange()
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    handleQueryChange() {
      const browserLocation = useBrowserLocation()
      const query = new URLSearchParams(browserLocation.value.search)
      const tabParam = query.get('tab')
      const selectedCategory = this.filter.categories.value.find(
        (item) => item.code === tabParam
      )

      if (
        selectedCategory &&
        selectedCategory.code !== this.filter.selectedItemCode.value
      ) {
        this.filter.setSelectedItem(selectedCategory)
      }
    },
    getSelectedMenuItem(): Nullable<MenuItem> {
      return (
        this.filter.categories.value.find(
          (m: MenuItem) => m.code === this.filter.selectedItemCode.value
        ) ?? null
      )
    },
    async updateFilter(menuItem: MenuItem) {
      const categoryIndex: number = this.filter.categories.value.findIndex(
        (m: MenuItem) => m.code === menuItem.code
      )

      if (categoryIndex === -1 || categoryIndex === this.current) {
        return
      }

      this.current = categoryIndex
      this.filter.setSelectedItem(menuItem)

      this.$emit('updateFilter', categoryIndex)

      // Ensure the newly selected menu item is visible in the scrollbox
      if (this.scrollbox) {
        if (!this.scrollbox.getVisibleIndexes().includes(categoryIndex)) {
          this.scrollbox.scrollTo(categoryIndex)
        }
      }
    },
  },
})
</script>

<template>
  <CarouselScrollBox ref="scrollbox" class="border-b border-slate-200 filter">
    <CarouselItem
      v-for="(menuItem, key) in filter.categories.value"
      :id="filter.selectedItemCode.value"
      :key
      class="swiper-slide !flex-1 !px-0"
    >
      <slot
        name="default"
        :item="menuItem"
        :update-filter
        :selected="current === key"
      >
        <AppFilterMenuItem
          :menu-item
          :class="[
            current === key
              ? 'selected border-tertiary'
              : 'border-b-transparent',
          ]"
          @click="updateFilter(menuItem)"
        />
      </slot>
    </CarouselItem>
  </CarouselScrollBox>
</template>

<style lang="scss" scoped>
.filter:deep(.filter-menu-item.selected *) {
  @apply font-bold;
}
</style>
